/**
 * Sluice Cloud SPA entry point.
 *
 * Import order here is load-bearing and is not a style choice:
 *
 *   1. The design-system stylesheets first. esbuild derives the entry's
 *      companion CSS bundle from exactly these imports, and ui/build/main.go
 *      fails the build if the entry produced no `cssBundle` — so moving them
 *      below the JavaScript imports would ship a stylesheet-less SPA.
 *   2. The theme module next, for its side effect: it writes the stored
 *      `data-theme` onto <html> at evaluation time, before any element is
 *      defined, so an explicit light or dark choice never flashes the other
 *      theme on first paint.
 *   3. The components the shell renders, then the shell itself.
 */
import "./shared/tokens.css";
import "./shared/fonts.css";
import "./shared/base.css";

// The theme module applies the stored `data-theme` at evaluation time, so the
// attribute is on <html> before the shell's first render and an explicit
// light/dark choice never flashes the other theme. It must therefore be
// imported for its side effect, before any element is defined.
import "./theme.js";

// The whole `bn-*` kit, registered once. The barrel is the ancestor's shape
// (`cloud/ui/src/main.ts` line 4) and it is what keeps a page from silently
// depending on whichever components some other page happened to import: a route
// that renders today would otherwise break the moment that other page is
// dropped. bn-code is in here too, but CodeMirror is not — the editor is behind
// a dynamic import and lands in its own chunk.
import "./components/index.js";

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@lit-labs/router";
import type { BnSelectChangeDetail, BnSelectOption } from "./components/index.js";
import { pb } from "./api/client.js";
import { parseError } from "./api/errors.js";
import { refreshSession, signOut } from "./api/auth.js";
import { isStaffPrincipal } from "./api/backoffice.js";
import { listOrgs, type Organization } from "./api/orgs.js";
import { getStatus, isQuiet, type PlatformStatus } from "./api/status.js";
import { bindRouter, hrefFor, isActive, navigate } from "./nav.js";
import { fallback, guard, routes } from "./router.js";

// The approval inbox (`SC-F810`). It is routed from HERE rather than from
// `router.ts`'s table, using the extension point that file documents — "`routes` is
// a mutable export: push a RouteConfig onto it" — because the shell is the one
// module that already owns both the table and the console navigation this surface
// needs an entry in, and a route added in two places is a route that eventually
// disagrees with itself.
//
// The push runs at module evaluation, before any element is constructed, so it is in
// place by the time the shell's `Router` is built from the same array. It is appended
// rather than inserted: `URLPattern` does not match `/instances/:id` against a longer
// path, so the overview cannot swallow it.
import "./pages/page-approvals.js";
routes.push({
  path: "/instances/:id/approvals",
  enter: guard,
  render: ({ id }) => html`<page-approvals .instanceId=${id ?? ""}></page-approvals>`,
});

// `/api-reference` (`SC-F202`) and `/device` (`SC-F217`) register themselves through
// the same extension point, and they need this import to exist at all.
//
// **A `routes.push` in a module nobody imports is dead code, and esbuild proves it by
// removing the module entirely.** Before this line, both pages compiled, type-checked
// and were absent from `dist/assets/main-*.js` — a route that answers 200 from the
// server, a custom element that is never defined, and a page that renders as an empty
// tag. It is a side-effect import for the same reason `page-approvals.js` above is
// one: the module's value is what it does when it is evaluated, not what it exports.
//
// It is imported here rather than from `router.ts` because that file's own header
// reserves its table for pages it imports directly and names `routes.push` as the way
// everything else lands. The shell is where the other pushed route already lives.
import "./pages/extra-routes.js";

/** One entry in the primary navigation. */
interface NavItem {
  href: string;
  label: string;
  /** Path prefixes (besides `href`) that also mark this item current. */
  match?: string[];
}

/**
 * Where the organization switcher's choice is remembered.
 *
 * `SC-S101` requires the current organization to be visible on **any** page and
 * "not inferable only from the URL" — but `/api-keys`, `/security` and
 * `/instances` carry no organization, and there is deliberately no server-side
 * current-organization session state (`SC-S101` notes: the organization is
 * always in the request). Something client-side has to remember the choice, and
 * this is it. It is a display preference, not authority: every route re-derives
 * membership from the credential, so a stale id here can only ever mislabel the
 * chrome for one render before the roster corrects it.
 */
const ORG_PIN_KEY = "sluice-cloud.org";

/**
 * How often the shell re-reads `GET /v1/status` (`SC-F907`).
 *
 * Sixty seconds, matching the control plane's own alert-delivery sweep, so the banner
 * and the outage email cannot be more than one sweep apart — a customer must never be
 * reading a page that says nothing is wrong while the mail about it is already in
 * their inbox. The request is one indexed read and is served anonymously, so the cost
 * is a request per minute per open tab.
 */
const STATUS_POLL_MS = 60_000;

const NAV: NavItem[] = [
  { href: "/instances", label: "Instances", match: ["/instances", "/jobs", "/"] },
  { href: "/orgs", label: "Organization", match: ["/orgs"] },
  { href: "/api-keys", label: "API keys", match: ["/api-keys"] },
  { href: "/security", label: "Security", match: ["/security"] },
];

/** One surface of the instance console. See [CONSOLE_NAV]. */
interface ConsoleItem {
  /** Path segment under `/instances/:id`, or the empty string for the overview. */
  segment: string;
  label: string;
  /** Extra segments that also mark this item current, for a surface reached from
   * another rather than from the nav. */
  also?: string[];
}

/**
 * CONSOLE_NAV is the instance console's own navigation.
 *
 * `router.ts` gives each of these surfaces a path of its own — they are separate
 * pages, not tabs of one element — so without a lateral nav the only way between
 * them is the `← Instance` link every one of them renders and then a second click.
 * This row is that missing edge, and it lives in the chrome rather than in a page
 * for the same reason the organization switcher does: fourteen copies of a tab
 * strip is how one of them ends up spelling a route differently from the router.
 *
 * **The order is the workflow**, not the alphabet: configure (datasources,
 * policies, bindings, server settings), ship (publish, generations), check
 * (validation, explain, tests, playground), then observe (audit, analytics,
 * operations).
 *
 * Two items joined the row when their pages landed. `Server settings` sits with the
 * other authoring surfaces because that is what it is — the customer-owned half of
 * `server.yaml` as a form — and `Operations` sits at the end with the observation
 * surfaces, because a job history is a record of what happened rather than a thing
 * to change. `Settings` is deliberately NOT here: it is the instance's own identity,
 * size and privacy profile, which is a per-instance administrative surface rather
 * than part of the authoring workflow, and it is reached from the console's own
 * settings tab and from server settings.
 */
const CONSOLE_NAV: ConsoleItem[] = [
  { segment: "", label: "Overview" },
  { segment: "datasources", label: "Datasources" },
  { segment: "policies", label: "Policies" },
  { segment: "subject-bindings", label: "Subject bindings" },
  { segment: "server-config", label: "Server settings" },
  { segment: "publish", label: "Publish" },
  // The diff is reached from a generation row and from the publish screen, and it
  // is about two generations, so it lights this item rather than adding a
  // fifteenth that nothing navigates to directly.
  { segment: "generations", label: "Generations", also: ["diff"] },
  { segment: "validation", label: "Validation" },
  { segment: "explain", label: "Explain" },
  { segment: "tests", label: "Tests" },
  { segment: "playground", label: "Playground" },
  { segment: "audit", label: "Audit" },
  { segment: "analytics", label: "Analytics" },
  // Approvals sits with the observation surfaces and not with the authoring ones,
  // which is a claim about what the screen is: a queue of things that have already
  // happened and are waiting on a person, not a place where policy is written. The
  // policy that produces its rows is authored under `Policies` (`SC-F519`).
  { segment: "approvals", label: "Approvals" },
  { segment: "jobs", label: "Operations" },
];

/**
 * sluice-cloud-app is the application shell: a sticky top bar (brand mark,
 * primary navigation, the theme control and the signed-in identity), the routed
 * outlet, and the single toast host.
 *
 * It owns the one top-level Router, which intercepts in-app link clicks and
 * browser navigation and re-renders both the outlet and this shell on every
 * route change — so the current nav item and the auth state are derived from
 * `location` at render time rather than mirrored into state that can drift. It
 * also subscribes to the auth store, so signing in or out re-renders the chrome;
 * anonymous callers are bounced to `/sign-in` by the per-route guard, not here.
 *
 * **The organization switcher** (`SC-F109`, `SC-S101`) is part of the chrome
 * rather than of any page, because its whole job is to make the current
 * organization unambiguous on screens whose URL does not name one. Switching
 * lands on the equivalent route in the new organization where one exists — every
 * `/orgs/:orgId/*` path is the same surface under a different id — and on that
 * organization's instance list otherwise. With exactly one organization it stays
 * on screen as a label: the requirement is that the context is never ambiguous,
 * not that it is always changeable.
 *
 * **The backoffice is not a nav item for customers.** A staff principal is a
 * separate identity with no organization membership at all (`01` §0.1), so the
 * shell asks [isStaffPrincipal] exactly when the roster is empty and shows the
 * console only on a yes. It is not a security control — `SC-F915`'s middleware
 * is, and it refuses every customer credential class by name — it is what keeps
 * a cross-tenant console out of a customer's field of view.
 *
 * It takes no properties: it is the root element, mounted by `index.html`, and
 * everything it needs comes from `location`, the auth store and the theme
 * module.
 *
 *   <sluice-cloud-app></sluice-cloud-app>
 */
@customElement("sluice-cloud-app")
export class SluiceCloudApp extends LitElement {
  static override styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: var(--bino-bg);
      color: var(--bino-text);
      font-family: var(--bino-font-sans);
    }

    /* Keyboard users reach the outlet without tabbing the whole nav. Visible
       only while focused, which is the point. */
    .skip {
      position: absolute;
      left: var(--bino-space-sm);
      top: -3rem;
      z-index: var(--bino-z-modal);
      padding: var(--bino-space-sm) var(--bino-space-md);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface);
      color: var(--bino-text);
      border: 1px solid var(--bino-border);
      box-shadow: var(--bino-shadow-dropdown);
      font-size: var(--bino-font-size-md);
      text-decoration: none;
      transition: top var(--bino-transition-fast);
    }
    .skip:focus-visible {
      top: var(--bino-space-sm);
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }

    header {
      position: sticky;
      top: 0;
      z-index: var(--bino-z-toolbar);
      background: var(--bino-surface);
      border-bottom: 1px solid var(--bino-border);
      box-shadow: var(--bino-shadow-header);
    }
    .bar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-lg);
      height: var(--bino-toolbar-height);
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 var(--bino-space-lg);
    }
    .brand {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .brand:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .glyph {
      width: 1.5rem;
      height: 1.5rem;
      border-radius: var(--bino-radius-sm);
      background: var(--bino-primary);
      color: var(--bino-on-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      font-weight: 800;
      line-height: 1;
    }
    .brand-label {
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
      white-space: nowrap;
    }

    /* The organization switcher. It sits between the brand and the sections
       because it scopes them: everything to its right is read inside whichever
       organization it names. */
    .org {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      min-width: 0;
    }
    .org::before {
      content: "/";
      color: var(--bino-border-hover);
      font-size: var(--bino-font-size-md);
    }
    .org bn-select {
      width: 13rem;
    }
    /* One organization: a label, not a control. There is nothing to switch to,
       and a dropdown with a single entry invites a click that does nothing. */
    .org-label {
      max-width: 13rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }

    nav.tabs {
      display: flex;
      align-items: center;
      gap: var(--bino-space-xs);
    }

    /* The instance console's second row. It is a subordinate nav, so it is drawn
       subordinate: a subtler surface, a hairline above it, and it pans rather than
       wraps — twelve items reflowing to three lines would move the whole page down
       every time the viewport narrowed. */
    .console {
      border-top: 1px solid var(--bino-border);
      background: var(--bino-surface-subtle);
    }
    .console nav.tabs {
      max-width: 1280px;
      margin: 0 auto;
      padding: var(--bino-space-xs) var(--bino-space-lg);
      overflow-x: auto;
      scrollbar-width: none;
    }
    .console nav.tabs::-webkit-scrollbar {
      display: none;
    }

    /* The staff console. Drawn in the warning tone that the console itself
       wears, so a cross-tenant surface never looks like a customer one — this is
       the only place in the chrome that is coloured at all. */
    .staff {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      text-decoration: none;
      white-space: nowrap;
    }
    .staff:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .tab {
      font-size: var(--bino-font-size-md);
      font-weight: 500;
      color: var(--bino-text-secondary);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      border-radius: var(--bino-radius-sm);
      text-decoration: none;
      white-space: nowrap;
      transition:
        background var(--bino-transition-fast),
        color var(--bino-transition-fast);
    }
    .tab:hover {
      color: var(--bino-text);
      background: var(--bino-surface-hover);
    }
    .tab:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .tab[aria-current="page"] {
      color: var(--bino-active-text);
      font-weight: 600;
      background: var(--bino-surface-active);
    }

    .spacer {
      flex: 1;
    }
    .who {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .email {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 18rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* The platform incident banner (SC-F907). Full width and directly under the
       header, above the routed outlet, so it is the first thing on every page and
       cannot be scrolled past before it is read. It is NOT sticky: it would then
       compete with the header for the top of a long page, and an incident notice that
       covers content is a notice people close. */
    .platform {
      border-bottom: 1px solid var(--bino-border);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }
    .platform.outage {
      background: var(--bino-error-bg);
      border-bottom-color: var(--bino-error-border);
      color: var(--bino-text);
    }
    .platform-bar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      max-width: 1280px;
      margin: 0 auto;
      padding: var(--bino-space-sm) var(--bino-space-lg);
    }
    .platform-headline {
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      min-width: 0;
    }
    .platform-detail {
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 var(--bino-space-lg) var(--bino-space-md);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .platform-item p,
    .platform-detail > p {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
    }
    .platform-meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }

    main {
      max-width: 1280px;
      margin: 0 auto;
      padding: var(--bino-space-xl) var(--bino-space-lg) 5rem;
    }

    @media (max-width: 760px) {
      .bar {
        flex-wrap: wrap;
        gap: var(--bino-space-md);
        row-gap: 0;
        height: auto;
        min-height: var(--bino-toolbar-height);
        padding: var(--bino-space-xs) var(--bino-space-md);
      }
      /* The nav drops to a full-width second row and pans horizontally rather
         than widening the document; brand, theme control and sign-out stay on
         row one. */
      nav.tabs {
        order: 1;
        flex-basis: 100%;
        min-width: 0;
        overflow-x: auto;
        scrollbar-width: none;
      }
      nav.tabs::-webkit-scrollbar {
        display: none;
      }
      .email {
        display: none;
      }
      .console nav.tabs {
        padding: var(--bino-space-xs) var(--bino-space-md);
      }
      .org bn-select,
      .org-label {
        width: 9rem;
        max-width: 9rem;
      }
      .platform-bar,
      .platform-detail {
        padding-left: var(--bino-space-md);
        padding-right: var(--bino-space-md);
      }
      .platform-bar {
        flex-wrap: wrap;
      }
      main {
        padding: var(--bino-space-lg) var(--bino-space-md) 4rem;
      }
    }
  `;

  /** The caller's organizations, for the switcher (`SC-F109`). Empty until the
   * roster lands, and empty for a staff principal by construction. */
  @state() private orgs: Organization[] = [];
  /** True once the backoffice probe has answered yes. See the class doc. */
  @state() private staff = false;
  /** The switcher's remembered choice, for routes that name no organization. */
  @state() private pinnedOrg = readPin();
  /**
   * The platform status (`SC-F907`), or undefined until the first read lands or
   * after one fails. Undefined renders no banner — see [renderStatusBanner].
   */
  @state() private platform?: PlatformStatus;
  /** Whether the banner's detail disclosure is open. Collapsed by default: the
   * headline is what everybody needs and the detail is what a few people want. */
  @state() private platformOpen = false;

  private router = new Router(this, routes, { fallback });

  private unsubscribe?: () => void;
  /** The status poll's interval handle, cleared on disconnect. */
  private statusTimer?: number;
  /** Guards the roster load against the several auth-store events one sign-in
   * produces. Reset on sign-out so the next account loads its own roster. */
  private contextLoaded = false;

  override connectedCallback() {
    super.connectedCallback();
    bindRouter(this.router);
    // The chrome derives everything from the auth store at render time, so the
    // subscription only has to ask for a re-render — there is no mirrored state
    // that could drift out of step with the store. The one thing it cannot
    // derive is the organization roster, which is a request; hence the reload.
    this.unsubscribe = pb.authStore.onChange(() => {
      this.requestUpdate();
      void this.loadContext();
    });
    void this.slideSession();
    void this.loadContext();
    void this.loadPlatformStatus();
    // Polled rather than read once, because the case that matters is the incident
    // that STARTS while the tab is open: a customer who loaded the app before the
    // outage would otherwise sit in front of a page that has nothing to say. Sixty
    // seconds matches the control plane's own delivery sweep, so the banner and the
    // outage mail cannot be more than a sweep apart.
    this.statusTimer = window.setInterval(() => void this.loadPlatformStatus(), STATUS_POLL_MS);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    if (this.statusTimer !== undefined) {
      window.clearInterval(this.statusTimer);
      this.statusTimer = undefined;
    }
  }

  /**
   * loadPlatformStatus reads `GET /v1/status`, anonymously.
   *
   * A failed read leaves the banner ABSENT rather than showing an error, which is the
   * same call [loadContext] makes about the organization roster and for the same
   * reason: the chrome is not the page, and blanking the shell over a status request
   * would cost the reader the product. The case that must never be silent — the
   * server being unable to read its own incident record — is not silent, because the
   * server answers `status: "unknown"` with a note rather than failing the request.
   */
  private async loadPlatformStatus() {
    try {
      this.platform = await getStatus();
    } catch {
      // Deliberately swallowed. See the doc comment.
    }
  }

  /**
   * loadContext reads the organization roster the switcher is built from, and
   * — only when that roster is empty — asks whether this credential is a staff
   * principal.
   *
   * A failed roster read is deliberately not an error state. The chrome is not
   * the page: losing the switcher costs the reader a label, while blanking the
   * shell would cost them the product. Every page below re-reads what it needs
   * and renders its own failure with the `request_id` on it.
   */
  private async loadContext() {
    if (!pb.authStore.isValid) {
      this.contextLoaded = false;
      this.orgs = [];
      this.staff = false;
      return;
    }
    if (this.contextLoaded) return;
    this.contextLoaded = true;
    let roster: Organization[];
    try {
      roster = (await listOrgs()).items;
    } catch {
      // Clear the guard so the next auth event tries again, and do not go on to
      // the staff probe: "the roster could not be read" is not "the roster is
      // empty", and only the second one is a question about staff.
      this.contextLoaded = false;
      this.orgs = [];
      return;
    }
    this.orgs = roster;
    // Every customer holds at least a personal organization (`SC-F101`), so an
    // empty roster is the only shape a staff principal can have — and asking
    // only here spares every customer session a request whose answer is fixed.
    if (roster.length === 0) this.staff = await isStaffPrincipal();
  }

  /**
   * slideSession renews the session token on every boot. Session tokens are
   * deliberately short-lived (24 h, `SC-F009`) so a leaked one stops working
   * quickly — but a fixed TTL would also expire under an active user
   * mid-session. Refreshing at boot slides the window forward: anyone who opens
   * the app at least once a day never sees the expiry, while an idle-for-a-day
   * session is genuinely gone.
   *
   * A 401 or 403 means the server rejected the token — expired, or revoked by
   * sign-out-everywhere from another device. Clearing the store and re-entering
   * the current route lets the guard bounce to `/sign-in` (preserving `?next=`)
   * instead of leaving the SPA rendering chrome around a dead session. Every
   * other failure is left alone: the stored token may be perfectly valid, and
   * signing a user out over a flaky network is worse than letting their next
   * real request surface the error.
   */
  private async slideSession() {
    if (!pb.authStore.isValid) return;
    try {
      await refreshSession();
    } catch (err) {
      const { status } = parseError(err);
      if (status !== 401 && status !== 403) return;
      signOut();
      void this.router.goto(location.pathname);
    }
  }

  private isCurrent(item: NavItem): boolean {
    return (item.match ?? [item.href]).some(isActive);
  }

  /**
   * consoleInstanceId is the instance whose console is on screen, or the empty
   * string when none is.
   *
   * `/instances/new` is excluded by name because "new" is not an id — the route
   * table puts that literal before `/instances/:id` for exactly this reason, and a
   * console nav on the create form would offer twelve links into an instance that
   * does not exist yet.
   */
  private get consoleInstanceId(): string {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments[0] !== "instances") return "";
    const id = segments[1];
    if (!id || id === "new") return "";
    return decodeURIComponent(id);
  }

  /**
   * The instance console's navigation row. Rendered only inside an instance, and
   * only for a signed-in caller — a staff principal never sees it, because they
   * hold no organization membership and every one of these routes would refuse.
   *
   * The overview is current only on an exact match: it is the prefix of every other
   * path here, so a prefix test would light it up on all twelve surfaces.
   */
  private renderConsoleNav() {
    const id = this.consoleInstanceId;
    if (id === "" || this.staff) return nothing;
    const base = hrefFor("instances", id);
    return html`
      <div class="console">
        <nav class="tabs" aria-label="Instance console">
          ${CONSOLE_NAV.map((item) => {
            const href = item.segment === "" ? base : `${base}/${item.segment}`;
            const current =
              item.segment === ""
                ? location.pathname === base
                : [item.segment, ...(item.also ?? [])].some((s) => isActive(`${base}/${s}`));
            return html`
              <a class="tab" href=${href} aria-current=${current ? "page" : "false"}
                >${item.label}</a
              >
            `;
          })}
        </nav>
      </div>
    `;
  }

  /**
   * currentOrg is the organization the chrome claims to be in, in the order the
   * evidence deserves: the id in the path if the route names one, then `?org=`
   * for the organization-scoped lists that carry it, then the remembered choice,
   * then the roster's first row.
   *
   * It may name an organization the caller does not belong to — a deep link into
   * somebody else's — which is why the switcher checks membership separately
   * rather than trusting this string (`SC-S101`).
   */
  private get currentOrg(): string {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments[0] === "orgs" && segments[1]) return decodeURIComponent(segments[1]);
    const scoped = new URLSearchParams(location.search).get("org");
    if (scoped) return scoped;
    if (this.pinnedOrg && this.orgs.some((o) => o.id === this.pinnedOrg)) return this.pinnedOrg;
    return this.orgs[0]?.id ?? "";
  }

  /**
   * onSwitchOrg is `SC-S101`'s switch rule: the equivalent route in the new
   * organization when the current route has one — every `/orgs/:orgId/*` path is
   * the same surface under a different id, so only the id changes and the query
   * string (the tab, a filter) is carried across — and that organization's
   * instance list otherwise, because it is where the work is.
   */
  private onSwitchOrg(id: string) {
    if (id === "" || id === this.currentOrg) return;
    this.pinnedOrg = id;
    writePin(id);
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments[0] === "orgs" && segments[1]) {
      const rest = segments.slice(2).map(encodeURIComponent).join("/");
      navigate(hrefFor("orgs", id) + (rest ? `/${rest}` : "") + location.search);
      return;
    }
    navigate(`/instances?org=${encodeURIComponent(id)}`);
  }

  /** The Instances and Organization tabs are organization-scoped, so they carry
   * the current organization with them — but only when there is more than one to
   * be ambiguous between. */
  private hrefWithOrg(item: NavItem): string {
    const org = this.currentOrg;
    if (org === "" || this.orgs.length < 2) {
      return item.href === "/orgs" && org !== "" ? hrefFor("orgs", org) : item.href;
    }
    if (item.href === "/orgs") return hrefFor("orgs", org);
    if (item.href === "/instances") return `/instances?org=${encodeURIComponent(org)}`;
    return item.href;
  }

  private onSignOut() {
    signOut();
    navigate("/sign-in");
  }

  /**
   * The organization switcher. It renders in three shapes, and each of them is
   * required by `SC-S101`: a picker when there is a choice, a plain label when
   * there is exactly one organization — the context must never be ambiguous,
   * even when it cannot change — and, for an id the roster does not contain, a
   * picker that offers the caller's own organizations and says that the one on
   * screen is not among them, rather than silently listing it as if it were.
   */
  private renderOrgSwitcher() {
    if (this.staff || this.orgs.length === 0) return nothing;
    const current = this.currentOrg;
    const member = this.orgs.some((o) => o.id === current);
    if (this.orgs.length === 1) {
      const only = this.orgs[0]!;
      const name = only.display_name || only.name;
      return html`<div class="org">
        <span class="org-label" title=${`Organization: ${name}`}>${name}</span>
      </div>`;
    }
    const options: BnSelectOption[] = this.orgs.map((o) => ({
      value: o.id,
      label: o.display_name || o.name,
      description: o.personal ? "Your personal organization" : o.name,
    }));
    return html`<div class="org">
      <bn-select
        aria-label="Organization"
        .options=${options}
        .value=${member ? current : ""}
        placeholder=${member ? "Organization" : "Not one of your organizations"}
        ?filterable=${this.orgs.length > 10}
        @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.onSwitchOrg(e.detail.value)}
      ></bn-select>
    </div>`;
  }

  /**
   * onSkip moves focus to the outlet. A plain `href="#content"` cannot do this:
   * fragment navigation resolves ids against the light-DOM document, and
   * `<main>` lives in this element's shadow root, so the browser would find
   * nothing and the skip link would silently do nothing at all.
   */
  private onSkip(e: Event) {
    e.preventDefault();
    this.renderRoot.querySelector<HTMLElement>("#content")?.focus();
  }

  /**
   * The primary navigation. A staff principal gets the console and nothing else:
   * they hold no organization membership, so Instances, Organization and API
   * keys would every one of them be an empty screen or a refusal, and offering
   * them would suggest a staff identity is a customer identity with extras. It
   * is not — it is a different principal with a different store (`01` §0.1).
   *
   * `/_kit` is deliberately absent: it is a development surface.
   */
  private renderTabs() {
    if (this.staff) {
      return html`<nav class="tabs" aria-label="Primary">
        <a class="staff" href="/backoffice" aria-current=${isActive("/backoffice") ? "page" : "false"}>
          <span aria-hidden="true">⚠</span> Backoffice
        </a>
      </nav>`;
    }
    return html`<nav class="tabs" aria-label="Primary">
      ${NAV.map(
        (item) => html`
          <a
            class="tab"
            href=${this.hrefWithOrg(item)}
            aria-current=${this.isCurrent(item) ? "page" : "false"}
            >${item.label}</a
          >
        `,
      )}
    </nav>`;
  }

  /**
   * The platform incident banner (`SC-F907`).
   *
   * # Why it lives in the chrome and not on a page
   *
   * Because a customer must not be able to miss it by being on a different screen.
   * `SC-F824` mails the organization's owners and maintainers about THEIR instance;
   * this is the other half — the fleet-wide statement, on every page, for everyone
   * including a viewer and including somebody who cannot sign in. The SPA's route
   * table has no `/status` path, so the detail a status page would carry is the
   * disclosure inside this banner rather than a second surface that could disagree
   * with it.
   *
   * # The four states, for a surface that is chrome rather than a page
   *
   * The repository's four-state rule (loading, empty, error, populated) is written for
   * pages. This is the chrome, and each state maps deliberately:
   *
   *   - **loading** renders nothing. A spinner in the header on every page load, for a
   *     request whose answer is almost always "nothing is wrong", is noise.
   *   - **empty** — `status: "operational"` — renders nothing, and the server says so
   *     explicitly in `notes` so an API reader is not left inferring it.
   *   - **error** renders nothing, because a failed status read must not blank the
   *     shell (see [loadPlatformStatus]). The server-side failure it might have
   *     signalled is not lost: that answers `unknown`, which renders below.
   *   - **populated** is this banner, and `role="alert"` on it is `SC-S712`'s pattern
   *     for the one other banner in the product that carries this weight.
   *
   * # `unknown` is not `operational`
   *
   * The one branch worth reading twice. When the control plane cannot read its own
   * incident record it answers `unknown`, and the banner says we cannot confirm that
   * nothing is wrong. Rendering that as green would be the product lying about the one
   * thing this feature exists to be honest about.
   */
  private renderStatusBanner() {
    const status = this.platform;
    if (isQuiet(status) || status === undefined) return nothing;

    const tone = status.status === "outage" ? "outage" : "degraded";
    const headline =
      status.incidents[0]?.title ??
      status.derived[0]?.summary ??
      (status.status === "unknown"
        ? "We cannot currently confirm the platform's status"
        : "Sluice Cloud is reporting a problem");
    const label =
      status.status === "unknown"
        ? "Status unknown"
        : status.status === "outage"
          ? "Outage"
          : status.status === "maintenance"
            ? "Maintenance"
            : "Degraded";
    const count = status.incidents.length + status.derived.length;

    return html`
      <div class="platform ${tone}" role="alert">
        <div class="platform-bar">
          <bn-badge variant=${status.status === "outage" ? "error" : "warning"}>${label}</bn-badge>
          <span class="platform-headline">${headline}</span>
          <span class="spacer"></span>
          <bn-button
            variant="ghost"
            size="sm"
            aria-expanded=${this.platformOpen ? "true" : "false"}
            @click=${() => {
              this.platformOpen = !this.platformOpen;
            }}
            >${this.platformOpen ? "Hide details" : `Details${count > 1 ? ` (${count})` : ""}`}</bn-button
          >
        </div>
        ${this.platformOpen ? this.renderStatusDetail(status) : nothing}
      </div>
    `;
  }

  /**
   * The banner's disclosure: every open notice, every derived signal, the honest
   * notes the API returns, and the shared failure domains that explain what each
   * notice's `domain` means.
   *
   * The notes and the blast-radius copy are rendered VERBATIM from the response
   * rather than restated here. `concept.md` §7 principle 6 — the UI never does
   * anything the API cannot — cuts both ways: a customer's own dashboard built on
   * `/v1/status` should show the same explanation ours does, and copy that lived only
   * in this file could not be the same.
   */
  private renderStatusDetail(status: PlatformStatus) {
    const domains = new Map(status.shared_failure_domains.map((d) => [d.domain, d]));
    return html`
      <div class="platform-detail">
        ${status.incidents.map(
          (inc) => html`
            <div class="platform-item">
              <strong>${inc.title}</strong>
              <p>${inc.body}</p>
              <p class="platform-meta">
                Since ${inc.started_at} · ${inc.severity} ·
                ${domains.get(inc.domain)?.blast_radius ?? inc.domain}
              </p>
            </div>
          `,
        )}
        ${status.derived.map(
          (sig) => html`
            <div class="platform-item">
              <strong>${sig.id}</strong>
              <p>${sig.summary}</p>
              <p class="platform-meta">
                Detected automatically from the control plane's own state, not written by an
                operator.
              </p>
            </div>
          `,
        )}
        ${status.notes.map((note) => html`<p class="platform-meta">${note}</p>`)}
        <p class="platform-meta">
          Reported ${status.as_of} · control plane ${status.control_plane.version}
        </p>
      </div>
    `;
  }

  override render() {
    const authed = pb.authStore.isValid;
    const email = (pb.authStore.record?.email as string | undefined) ?? "";
    return html`
      <a class="skip" href="#content" @click=${this.onSkip}>Skip to content</a>
      <header>
        <div class="bar">
          <a class="brand" href="/" aria-label="Sluice Cloud home">
            <span class="glyph" aria-hidden="true">S</span>
            <span class="brand-label">Sluice Cloud</span>
          </a>
          ${authed ? this.renderOrgSwitcher() : nothing}
          ${authed ? this.renderTabs() : nothing}
          <span class="spacer"></span>
          <sluice-theme-toggle></sluice-theme-toggle>
          ${authed
            ? html`<div class="who">
                <span class="email" title=${email}>${email}</span>
                <bn-button variant="ghost" size="sm" @click=${this.onSignOut}>Sign out</bn-button>
              </div>`
            : html`<a class="tab" href="/sign-in">Sign in</a>`}
        </div>
        ${authed ? this.renderConsoleNav() : nothing}
      </header>
      ${this.renderStatusBanner()}
      <main id="content" tabindex="-1">${this.router.outlet()}</main>
      <bn-toaster></bn-toaster>
    `;
  }
}

/** readPin / writePin hold the switcher's choice across a reload. Both swallow
 * their failures: storage is unavailable in a locked-down browser profile and in
 * private mode on some engines, and losing a display preference must never be
 * the thing that stops the shell from rendering. */
function readPin(): string {
  try {
    return localStorage.getItem(ORG_PIN_KEY) ?? "";
  } catch {
    return "";
  }
}

function writePin(id: string): void {
  try {
    localStorage.setItem(ORG_PIN_KEY, id);
  } catch {
    /* not remembered; the URL still carries the choice for this navigation */
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sluice-cloud-app": SluiceCloudApp;
  }
}
